import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/constants";
import { useAuth } from "@/AuthContext";
import { toast } from "sonner";

const STATUS_STYLES = {
  baru: "bg-amber-100 text-amber-800 border-amber-300",
  diproses: "bg-blue-100 text-blue-800 border-blue-300",
  selesai: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

function SiteSettingsForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`, { withCredentials: true })
      .then((res) => setForm(res.data))
      .catch(() => toast.error("Gagal memuat info website."));
  }, [navigate]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, {
        address: form.address,
        whatsapp: form.whatsapp,
        tagline: form.tagline,
        heroTitle: form.heroTitle,
        heroSubtitle: form.heroSubtitle,
      }, { withCredentials: true });
      toast.success("Info website tersimpan. Perubahan langsung tampil di halaman utama.");
    } catch {
      toast.error("Gagal menyimpan info website.");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="text-sm text-slate-500 py-8 text-center" data-testid="settings-loading">Memuat info website...</div>;

  const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 bg-white";
  const labelCls = "block text-xs font-semibold text-slate-700 mb-1";

  return (
    <form onSubmit={handleSave} className="bg-white rounded-2xl border border-slate-200 p-6 max-w-2xl space-y-4" data-testid="settings-form">
      <div>
        <label htmlFor="set-hero-title" className={labelCls}>Judul Utama (Headline Hero)</label>
        <input id="set-hero-title" required value={form.heroTitle} onChange={(e) => setForm({ ...form, heroTitle: e.target.value })} className={inputCls} data-testid="settings-hero-title" />
      </div>
      <div>
        <label htmlFor="set-hero-subtitle" className={labelCls}>Teks Pembuka di Bawah Judul</label>
        <textarea id="set-hero-subtitle" rows="3" required value={form.heroSubtitle} onChange={(e) => setForm({ ...form, heroSubtitle: e.target.value })} className={inputCls} data-testid="settings-hero-subtitle"></textarea>
      </div>
      <div>
        <label htmlFor="set-tagline" className={labelCls}>Tagline di Bawah Logo</label>
        <input id="set-tagline" required value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className={inputCls} data-testid="settings-tagline" />
      </div>
      <div>
        <label htmlFor="set-address" className={labelCls}>Alamat Kantor</label>
        <input id="set-address" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} data-testid="settings-address" />
      </div>
      <div>
        <label htmlFor="set-whatsapp" className={labelCls}>Nomor WhatsApp (format 62xxx, contoh: 6285165458874)</label>
        <input id="set-whatsapp" required value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className={inputCls} data-testid="settings-whatsapp" />
      </div>
      <button type="submit" disabled={saving} className="px-6 py-3 rounded-xl bg-blue-900 text-amber-300 font-bold text-sm hover:bg-blue-950 transition-colors disabled:opacity-60" data-testid="settings-save-button">
        {saving ? "Menyimpan..." : "Simpan Info Website"}
      </button>
    </form>
  );
}

function ContentForm() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`, { withCredentials: true })
      .then((res) => setForm(res.data))
      .catch(() => toast.error("Gagal memuat konten halaman."));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, form, { withCredentials: true });
      toast.success("Konten halaman tersimpan. Perubahan langsung tampil di website.");
    } catch {
      toast.error("Gagal menyimpan konten. Periksa isian Anda.");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="text-sm text-slate-500 py-8 text-center" data-testid="content-loading">Memuat konten...</div>;

  const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 bg-white";
  const labelCls = "block text-xs font-semibold text-slate-700 mb-1";
  const services = Array.isArray(form.services) ? form.services : [];
  const faqs = Array.isArray(form.faqs) ? form.faqs : [];
  const promo = form.promo || { enabled: false, text: "", ctaText: "Minta Penawaran" };

  const setService = (i, patch) => {
    const next = services.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    setForm({ ...form, services: next });
  };

  const setFaq = (i, patch) => setForm({ ...form, faqs: faqs.map((f, idx) => (idx === i ? { ...f, ...patch } : f)) });

  return (
    <form onSubmit={handleSave} className="space-y-6 max-w-3xl" data-testid="content-form">
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6" data-testid="promo-editor">
        <h3 className="text-sm font-bold text-slate-900 mb-3">Banner Promo Musiman</h3>
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 mb-3">
          <input type="checkbox" checked={promo.enabled} onChange={(e) => setForm({ ...form, promo: { ...promo, enabled: e.target.checked } })} className="w-4 h-4 accent-blue-900" data-testid="promo-enabled" />
          Tampilkan banner promo di bagian atas website
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label htmlFor="promo-text" className={labelCls}>Teks Promo</label>
            <input id="promo-text" value={promo.text} onChange={(e) => setForm({ ...form, promo: { ...promo, text: e.target.value } })} className={inputCls} placeholder="Contoh: Promo bulan ini! Diskon 10% kiriman darat ke Palopo" data-testid="promo-text" />
          </div>
          <div>
            <label htmlFor="promo-cta" className={labelCls}>Teks Tombol</label>
            <input id="promo-cta" value={promo.ctaText} onChange={(e) => setForm({ ...form, promo: { ...promo, ctaText: e.target.value } })} className={inputCls} placeholder="Minta Penawaran" data-testid="promo-cta-text" />
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="services-editor">
        <h3 className="text-sm font-bold text-slate-900 mb-1">Tiga Kartu Moda Layanan</h3>
        <p className="text-[11px] text-slate-500 mb-4">Kartu ke-2 tampil sebagai kartu unggulan (biru). Poin layanan: satu poin per baris.</p>
        <div className="space-y-5">
          {services.map((s, i) => (
            <div key={i} className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3" data-testid={`service-editor-${i}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Judul Kartu {i + 1}</label>
                  <input required value={s.title} onChange={(e) => setService(i, { title: e.target.value })} className={inputCls} data-testid={`service-title-${i}`} />
                </div>
                <div>
                  <label className={labelCls}>Teks Tombol</label>
                  <input value={s.cta} onChange={(e) => setService(i, { cta: e.target.value })} className={inputCls} data-testid={`service-cta-${i}`} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Deskripsi</label>
                <textarea rows="2" required value={s.desc} onChange={(e) => setService(i, { desc: e.target.value })} className={inputCls} data-testid={`service-desc-${i}`}></textarea>
              </div>
              <div>
                <label className={labelCls}>Poin Layanan (satu per baris)</label>
                <textarea rows="3" value={(s.points || []).join("\n")} onChange={(e) => setService(i, { points: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} className={inputCls} data-testid={`service-points-${i}`}></textarea>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="faq-editor">
        <h3 className="text-sm font-bold text-slate-900 mb-4">Pertanyaan Umum (FAQ)</h3>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <div key={i} className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3" data-testid={`faq-editor-${i}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 space-y-3">
                  <div>
                    <label className={labelCls}>Pertanyaan {i + 1}</label>
                    <input required value={f.q} onChange={(e) => setFaq(i, { q: e.target.value })} className={inputCls} data-testid={`faq-q-${i}`} />
                  </div>
                  <div>
                    <label className={labelCls}>Jawaban</label>
                    <textarea rows="2" required value={f.a} onChange={(e) => setFaq(i, { a: e.target.value })} className={inputCls} data-testid={`faq-a-${i}`}></textarea>
                  </div>
                </div>
                <button type="button" onClick={() => setForm({ ...form, faqs: faqs.filter((_, idx) => idx !== i) })}
                  className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-[11px] font-bold hover:bg-red-50 transition-colors shrink-0"
                  data-testid={`faq-remove-${i}`}>
                  Hapus
                </button>
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setForm({ ...form, faqs: [...faqs, { q: "", a: "" }] })}
          className="mt-4 px-4 py-2 rounded-lg bg-slate-900 text-white text-xs font-bold hover:bg-slate-700 transition-colors"
          data-testid="faq-add">
          + Tambah Pertanyaan
        </button>
      </div>

      <button type="submit" disabled={saving} className="px-6 py-3 rounded-xl bg-blue-900 text-amber-300 font-bold text-sm hover:bg-blue-950 transition-colors disabled:opacity-60" data-testid="content-save-button">
        {saving ? "Menyimpan..." : "Simpan Konten Halaman"}
      </button>
    </form>
  );
}

function TariffManager() {
  const navigate = useNavigate();
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", region: "", rate: "", minKg: "", days: "", moda: "Darat", unit: "kg" });
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(null);

  const fetchTariffs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tariffs`, { withCredentials: true });
      setTariffs(data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
      else toast.error("Gagal memuat daftar tarif.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchTariffs(); }, [fetchTariffs]);

  const regions = [...new Set(tariffs.map((t) => t.region))];
  const inputCls = "px-2.5 py-1.5 rounded-lg border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-blue-900 bg-white";

  const rowValues = (t) => ({
    name: t.name,
    region: t.region,
    moda: t.moda || "Darat",
    unit: t.unit || "kg",
    days: edits[t.id]?.days ?? t.days,
    rate: parseInt(edits[t.id]?.rate ?? t.rate, 10),
    minKg: parseInt(edits[t.id]?.minKg ?? t.minKg, 10),
  });

  const handleSave = async (t) => {
    const payload = rowValues(t);
    if (!payload.rate || !payload.minKg) { toast.error("Tarif & minimal kg wajib angka."); return; }
    setSaving(t.id);
    try {
      const { data } = await axios.patch(`${API}/tariffs/${t.id}`, payload, { withCredentials: true });
      setTariffs((prev) => prev.map((x) => (x.id === t.id ? data : x)));
      setEdits((prev) => { const n = { ...prev }; delete n[t.id]; return n; });
      toast.success(`Tarif ${t.name} tersimpan.`);
    } catch {
      toast.error("Gagal menyimpan tarif.");
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Hapus tujuan "${t.name}" dari daftar tarif?`)) return;
    try {
      await axios.delete(`${API}/tariffs/${t.id}`, { withCredentials: true });
      setTariffs((prev) => prev.filter((x) => x.id !== t.id));
      toast.success(`Tarif ${t.name} dihapus.`);
    } catch {
      toast.error("Gagal menghapus tarif.");
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/tariffs`, {
        name: form.name.trim(),
        region: form.region.trim() || "Lainnya",
        rate: parseInt(form.rate, 10),
        minKg: parseInt(form.minKg, 10),
        days: form.days.trim(),
        moda: form.moda,
        unit: form.unit,
      }, { withCredentials: true });
      setTariffs((prev) => [...prev, data]);
      setForm({ name: "", region: "", rate: "", minKg: "", days: "", moda: "Darat", unit: "kg" });
      toast.success(`Tujuan ${data.name} ditambahkan.`);
    } catch {
      toast.error("Gagal menambah tujuan. Periksa isian Anda.");
    }
  };

  if (loading) return <div className="text-sm text-slate-500 py-8 text-center" data-testid="tariff-loading">Memuat tarif...</div>;

  return (
    <div data-testid="tariff-manager">
      <form onSubmit={handleAdd} className="bg-white rounded-2xl border border-slate-200 p-5 mb-6" data-testid="tariff-add-form">
        <h3 className="text-sm font-bold text-slate-900 mb-3">Tambah Kota Tujuan Baru</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <input required placeholder="Nama kota" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="tariff-add-name" />
          <input required placeholder="Wilayah" list="region-list" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className={inputCls} data-testid="tariff-add-region" />
          <datalist id="region-list">{regions.map((r) => <option key={r} value={r} />)}</datalist>
          <select value={form.moda} onChange={(e) => setForm({ ...form, moda: e.target.value })} className={inputCls} data-testid="tariff-add-moda">
            <option value="Darat">Darat</option>
            <option value="Laut">Laut</option>
            <option value="Udara">Udara</option>
          </select>
          <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className={inputCls} data-testid="tariff-add-unit">
            <option value="kg">Per kg</option>
            <option value="m3">Per m³ (kubik)</option>
          </select>
          <input required type="number" min="1" placeholder="Tarif per satuan (Rp)" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} className={inputCls} data-testid="tariff-add-rate" />
          <input required type="number" min="1" placeholder="Min. satuan" value={form.minKg} onChange={(e) => setForm({ ...form, minKg: e.target.value })} className={inputCls} data-testid="tariff-add-minkg" />
          <input required placeholder="Estimasi (mis: 2–3 hari)" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} className={inputCls} data-testid="tariff-add-days" />
          <button type="submit" className="rounded-lg bg-blue-900 text-amber-300 text-xs font-bold hover:bg-blue-950 transition-colors" data-testid="tariff-add-submit">Tambah</button>
        </div>
      </form>

      {regions.map((region) => (
        <div key={region} className="mb-6">
          <h3 className="text-xs font-bold uppercase tracking-widest text-blue-900 mb-2" data-testid={`tariff-region-${region.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{region}</h3>
          <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
            {tariffs.filter((t) => t.region === region).map((t) => (
              <div key={t.id} className="flex flex-wrap items-center gap-2 px-4 py-3" data-testid={`tariff-row-${t.id}`}>
                <span className="text-xs font-bold text-slate-900 w-40 truncate">{t.name}</span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${(t.moda || "Darat") === "Darat" ? "bg-amber-100 text-amber-800" : (t.moda === "Laut" ? "bg-blue-100 text-blue-800" : "bg-sky-100 text-sky-800")}`} data-testid={`tariff-moda-${t.id}`}>
                  {t.moda || "Darat"}{t.unit === "m3" ? "/m³" : "/kg"}
                </span>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-slate-500">Rp/kg</span>
                  <input type="number" min="1" value={edits[t.id]?.rate ?? t.rate}
                    onChange={(e) => setEdits((p) => ({ ...p, [t.id]: { ...p[t.id], rate: e.target.value } }))}
                    className={`${inputCls} w-24`} data-testid={`tariff-rate-${t.id}`} />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-slate-500">Min kg</span>
                  <input type="number" min="1" value={edits[t.id]?.minKg ?? t.minKg}
                    onChange={(e) => setEdits((p) => ({ ...p, [t.id]: { ...p[t.id], minKg: e.target.value } }))}
                    className={`${inputCls} w-20`} data-testid={`tariff-minkg-${t.id}`} />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-slate-500">Estimasi</span>
                  <input value={edits[t.id]?.days ?? t.days}
                    onChange={(e) => setEdits((p) => ({ ...p, [t.id]: { ...p[t.id], days: e.target.value } }))}
                    className={`${inputCls} w-24`} data-testid={`tariff-days-${t.id}`} />
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <button onClick={() => handleSave(t)} disabled={saving === t.id || !edits[t.id]}
                    className="px-3 py-1.5 rounded-lg bg-blue-900 text-amber-300 text-[11px] font-bold hover:bg-blue-950 transition-colors disabled:opacity-40"
                    data-testid={`tariff-save-${t.id}`}>
                    {saving === t.id ? "..." : "Simpan"}
                  </button>
                  <button onClick={() => handleDelete(t)}
                    className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-[11px] font-bold hover:bg-red-50 transition-colors"
                    data-testid={`tariff-delete-${t.id}`}>
                    Hapus
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("semua");
  const [tab, setTab] = useState("permintaan");

  const fetchRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/shipping-requests`, { withCredentials: true });
      setRequests(data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
      else toast.error("Gagal memuat data permintaan.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (user === false) { navigate("/admin/login"); return; }
    if (user) fetchRequests();
  }, [user, navigate, fetchRequests]);

  const handleStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/shipping-requests/${id}`, { status }, { withCredentials: true });
      setRequests((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success(`Status diubah menjadi "${status}".`);
    } catch {
      toast.error("Gagal mengubah status.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus permintaan ini secara permanen?")) return;
    try {
      await axios.delete(`${API}/shipping-requests/${id}`, { withCredentials: true });
      setRequests((prev) => prev.filter((r) => r.id !== id));
      toast.success("Permintaan dihapus.");
    } catch {
      toast.error("Gagal menghapus permintaan.");
    }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    setUser(false);
    navigate("/admin/login");
  };

  const counts = {
    semua: requests.length,
    baru: requests.filter((r) => r.status === "baru").length,
    diproses: requests.filter((r) => r.status === "diproses").length,
    selesai: requests.filter((r) => r.status === "selesai").length,
  };
  const visible = filter === "semua" ? requests : requests.filter((r) => r.status === filter);

  const exportCsv = () => {
    const rows = [
      ["Tanggal", "Nama", "WhatsApp", "Rute", "Moda", "Muatan", "Status"],
      ...visible.map((r) => [
        new Date(r.created_at).toLocaleString("id-ID"),
        r.sender_name, r.whatsapp, r.destination, r.route, r.cargo_details, r.status,
      ]),
    ];
    const csv = rows.map((row) => row.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `permintaan-nurrahma-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`CSV berisi ${visible.length} permintaan diunduh.`);
  };

  if (user === null || loading) {
    return <div className="min-h-screen bg-[#F4F7FB] flex items-center justify-center text-sm text-slate-500" data-testid="admin-loading">Memuat dashboard...</div>;
  }

  return (
    <div className="min-h-screen bg-[#F4F7FB]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="Nurrahma Logistics" className="h-9 w-auto" />
            <div>
              <span className="text-sm font-black text-blue-950 block leading-tight">Dashboard Permintaan</span>
              <span className="text-[11px] text-slate-500">{user.name} · {user.role === "admin" ? "Pemilik" : "Staf Operasional"}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs font-semibold text-slate-600 hover:text-blue-900 transition-colors" data-testid="admin-view-site">Lihat Website</Link>
            <button onClick={handleLogout} className="px-4 py-2 rounded-lg bg-slate-900 text-white text-xs font-bold hover:bg-slate-700 transition-colors" data-testid="admin-logout-button">
              Keluar
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-2 mb-6" data-testid="admin-tabs">
          <button onClick={() => setTab("permintaan")}
            className={`px-4 py-2 rounded-lg text-xs font-bold border transition-colors ${tab === "permintaan" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}
            data-testid="tab-permintaan">
            Permintaan Masuk
          </button>
          <button onClick={() => setTab("tarif")}
            className={`px-4 py-2 rounded-lg text-xs font-bold border transition-colors ${tab === "tarif" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}
            data-testid="tab-tarif">
            Kelola Tarif
          </button>
          <button onClick={() => setTab("info")}
            className={`px-4 py-2 rounded-lg text-xs font-bold border transition-colors ${tab === "info" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}
            data-testid="tab-info">
            Info Website
          </button>
          <button onClick={() => setTab("konten")}
            className={`px-4 py-2 rounded-lg text-xs font-bold border transition-colors ${tab === "konten" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}
            data-testid="tab-konten">
            Konten Halaman
          </button>
        </div>
        {tab === "permintaan" ? (<>
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="status-filters">
          {["semua", "baru", "diproses", "selesai"].map((s) => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-4 py-2 rounded-lg text-xs font-bold capitalize border transition-colors ${filter === s ? "bg-blue-900 text-amber-300 border-blue-900" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"}`}
              data-testid={`filter-${s}`}>
              {s} ({counts[s]})
            </button>
          ))}
          <button onClick={exportCsv} disabled={visible.length === 0}
            className="ml-auto px-4 py-2 rounded-lg text-xs font-bold bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-40"
            data-testid="export-csv-button">
            Unduh CSV ({visible.length})
          </button>
        </div>

        {visible.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-sm text-slate-500" data-testid="empty-state">
            Belum ada permintaan {filter !== "semua" ? `berstatus "${filter}"` : "yang masuk"}.
          </div>
        ) : (
          <div className="space-y-4">
            {visible.map((r) => (
              <div key={r.id} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid={`request-card-${r.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="text-base font-bold text-slate-900" data-testid={`request-name-${r.id}`}>{r.sender_name}</h3>
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_STYLES[r.status] || STATUS_STYLES.baru}`} data-testid={`request-status-${r.id}`}>
                        {r.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-xs text-slate-600">
                      <p><span className="font-semibold text-slate-800">WhatsApp:</span> <a href={`https://wa.me/${r.whatsapp.replace(/[^0-9]/g, "").replace(/^0/, "62")}`} target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-semibold hover:underline" data-testid={`request-wa-${r.id}`}>{r.whatsapp}</a></p>
                      <p><span className="font-semibold text-slate-800">Rute:</span> {r.destination}</p>
                      <p><span className="font-semibold text-slate-800">Moda:</span> {r.route}</p>
                      <p><span className="font-semibold text-slate-800">Masuk:</span> {new Date(r.created_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}</p>
                    </div>
                    <p className="mt-3 text-xs text-slate-700 bg-slate-50 border border-slate-100 rounded-lg p-3 leading-relaxed">
                      <span className="font-semibold text-slate-800">Muatan:</span> {r.cargo_details}
                    </p>
                  </div>
                  <div className="flex lg:flex-col items-center gap-2 shrink-0">
                    <select value={r.status} onChange={(e) => handleStatus(r.id, e.target.value)}
                      className="px-3 py-2 rounded-lg border border-slate-300 text-xs font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-blue-900"
                      data-testid={`request-status-select-${r.id}`}>
                      <option value="baru">Baru</option>
                      <option value="diproses">Diproses</option>
                      <option value="selesai">Selesai</option>
                    </select>
                    <button onClick={() => handleDelete(r.id)}
                      className="px-3 py-2 rounded-lg border border-red-200 text-red-600 text-xs font-bold hover:bg-red-50 transition-colors"
                      data-testid={`request-delete-${r.id}`}>
                      Hapus
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </>) : tab === "tarif" ? (<TariffManager />) : tab === "info" ? (<SiteSettingsForm />) : (<ContentForm />)}
      </main>
    </div>
  );
}
